import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Shield, MapPin, FileText, ExternalLink, ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import { HreflangTags, useSyncLanguageFromURL } from '../components/HreflangTags';
import SeoMeta from '../components/SeoMeta';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import CountryComparator from '../components/CountryComparator';
import {
  COUNTRY_FACTS,
  COUNTRY_SLUGS,
  FRANCE_OVERSEAS,
  getCountryContent,
} from '../data/countriesContent';
import { buildBreadcrumbJsonLd } from '../data/seoContent';
import { buildLocalizedPath, DEFAULT_LANGUAGE, extractLangFromPath } from '../lib/i18nRouting';
import { useLocation } from 'react-router-dom';

export default function CountryPage() {
  useSyncLanguageFromURL();
  const { country } = useParams();
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const lang = extractLangFromPath(location.pathname) || i18n.language || DEFAULT_LANGUAGE;
  const [openFaq, setOpenFaq] = useState(null);

  if (!COUNTRY_SLUGS.includes(country)) {
    return <Navigate to={buildLocalizedPath('/', lang)} replace />;
  }

  const content = getCountryContent(country, lang);
  const facts = COUNTRY_FACTS[country];
  if (!content) {
    return <Navigate to={buildLocalizedPath('/', lang)} replace />;
  }

  // Schema.org : FAQPage + BreadcrumbList + Article
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: 'Drone Maps', url: `https://drone-maps.eu${buildLocalizedPath('/', lang)}` },
    { name: content.name, url: `https://drone-maps.eu${buildLocalizedPath(`/regulations/${country}`, lang)}` },
  ]);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.h1,
    description: content.description,
    inLanguage: lang,
    author: { '@type': 'Organization', name: 'Drone Maps' },
    publisher: {
      '@type': 'Organization',
      name: 'Drone Maps',
      logo: { '@type': 'ImageObject', url: 'https://drone-maps.eu/logo.png' },
    },
    datePublished: '2026-02-01',
    dateModified: '2026-02-01',
  };

  const publicHeader = (
    <header className="bg-[#1b2236] text-white border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to={buildLocalizedPath('/', lang)} className="flex items-center gap-2" data-testid="country-header-logo">
            <img src="/logo-landscape.png" alt="Drone Maps" className="h-14 md:h-16 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to={buildLocalizedPath('/', lang)} className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
              {t('nav.features', 'Features')}
            </Link>
            <Link to={buildLocalizedPath('/blog', lang)} className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
              {t('nav.blog', 'Blog')}
            </Link>
            <Link to="/faq" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">FAQ</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <Link to="/register">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="country-cta-register">
                {t('landing.hero.cta', 'Get started')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );

  return (
    <PublicPageLayout publicHeader={publicHeader} title={content.name}>
      <div className="min-h-screen bg-slate-50">
        <HreflangTags />
        <SeoMeta
          title={content.title}
          description={content.description}
          keywords={content.keywords}
          type="article"
          rawTitle
          jsonLd={[faqJsonLd, breadcrumbJsonLd, articleJsonLd]}
        />

        {/* Hero */}
        <section className="bg-[#1b2236] py-16 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={buildLocalizedPath('/', lang)}
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-sm mb-6"
            data-testid="country-back-home"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('common.back', 'Back')} — Drone Maps
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-400 text-sm font-medium">
              <Shield className="w-4 h-4" /> EU 2019/947
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-medium">
              <MapPin className="w-4 h-4" /> {facts.authority}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-[Barlow] tracking-tight leading-tight mb-6" data-testid="country-h1">
            {content.h1}
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">{content.intro}</p>
        </div>
      </section>

      {/* Quick facts */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t('country.authority', 'Authority')}</div>
            <div className="font-semibold text-slate-900">{facts.authorityFull}</div>
            <a href={facts.authorityUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-sm mt-1">
              {facts.authority} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t('country.registration', 'Registration')}</div>
            <div className="font-semibold text-slate-900">{facts.registration}</div>
            <a href={facts.registrationUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-sm mt-1">
              {t('country.openPortal', 'Open portal')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t('country.notamSource', 'NOTAM source')}</div>
            <div className="font-semibold text-slate-900">{facts.notamSource}</div>
            <a href={facts.notamUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-sm mt-1">
              {t('country.viewMap', 'View map')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">AIP</div>
            <div className="font-semibold text-slate-900">{facts.iso}</div>
            <a href={facts.aipUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-sm mt-1">
              {t('country.aip', 'Official AIP')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Regulation overview */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900 mb-6">
            {t('country.overview', 'Regulatory overview')}
          </h2>
          <p className="text-slate-700 text-lg leading-relaxed">{content.regulationOverview}</p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {content.sections.map((section, idx) => (
            <article key={idx} className="border-l-4 border-sky-500 pl-6">
              <h3 className="text-xl sm:text-2xl font-bold font-[Barlow] text-slate-900 mb-3">
                {section.title}
              </h3>
              <p className="text-slate-700 leading-relaxed">{section.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900 mb-8">FAQ — {content.name}</h2>
          <div className="space-y-3">
            {content.faq.map((item, idx) => (
              <div key={idx} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50"
                  data-testid={`country-faq-toggle-${idx}`}
                >
                  <span className="font-semibold text-slate-900">{item.question}</span>
                  <span className="text-sky-500">{openFaq === idx ? '−' : '+'}</span>
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4 text-slate-700">{item.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory comparator — cross-links every country page to
          31 peers, boosting internal linking density and long-tail SEO
          for queries like "réglementation drone Espagne vs France". */}
      <CountryComparator currentSlug={country} lang={lang} />

      {/* DOM-TOM territories grid (only on france-outre-mer page) */}
      {country === 'france-outre-mer' && (
        <section className="py-16 bg-white border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900 mb-6">
              {t('country.overseasTerritories', 'Territoires couverts')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t('country.overseasIntro', 'Drone Maps charge automatiquement les zones UAS officielles ED-269 pour chaque territoire. Cliquez sur un territoire pour le centrer sur la carte interactive.')}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FRANCE_OVERSEAS.map((territory) => (
                <Link
                  key={territory.key}
                  to={`/?territory=${territory.key}`}
                  className="block p-4 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg transition-all group"
                  data-testid={`overseas-territory-${territory.key}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-sky-700">{territory.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">DOM-TOM {territory.code}</p>
                    </div>
                    <span className="inline-flex items-center text-xs font-mono bg-sky-100 text-sky-700 px-2 py-1 rounded">
                      {territory.zones} {t('country.zones', 'zones')}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>{territory.center[0].toFixed(2)}°, {territory.center[1].toFixed(2)}°</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related countries */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold font-[Barlow] text-slate-900 mb-6">
            {t('country.otherCountries', 'Other covered countries')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {COUNTRY_SLUGS.filter((s) => s !== country).map((slug) => {
              const otherContent = getCountryContent(slug, lang);
              if (!otherContent) return null;
              return (
                <Link
                  key={slug}
                  to={buildLocalizedPath(`/regulations/${slug}`, lang)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-sky-100 hover:text-sky-700 text-slate-700 text-sm font-medium transition-colors"
                  data-testid={`country-link-${slug}`}
                >
                  {otherContent.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1b2236] text-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-12 h-12 text-sky-400 mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold font-[Barlow] mb-4">
            {t('country.ctaTitle', 'Plan your next drone mission in')} {content.name}
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            {t(
              'country.ctaSubtitle',
              'Cross your mission with official ED-269 zones and generate a compliant PDF report in seconds.'
            )}
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8" data-testid="country-cta-bottom">
              {t('landing.hero.cta', 'Get started')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="bg-[#1b2236] text-slate-400 py-8 border-t border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-sm">
          <p>© Drone Maps — {t('footer.tagline', 'European drone compliance platform')}</p>
          <div className="flex gap-4">
            <Link to="/mentions-legales" className="hover:text-white">{t('footer.legal', 'Legal')}</Link>
            <Link to="/cgu" className="hover:text-white">CGU</Link>
            <Link to="/confidentialite" className="hover:text-white">{t('footer.privacy', 'Privacy')}</Link>
          </div>
        </div>
      </footer>
      </div>
    </PublicPageLayout>
  );
}
